import { useState, useCallback, useMemo, useEffect } from "react";
import { FontSizePreference } from "../../../entities-generated/common/font-size-preference";
import { IPresenterAccountPreferences, PresenterAccountPreferences } from "../../../entities-generated/common/presenter-account-preferences";
import { TimeDisplayFormat } from "../../../entities-generated/common/time-display-format";
import { IBwjFormOption } from "../../../framework/entities/bwj-form-option";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ValidatedFormValueService, MutableFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged } from "../../../framework/utils";
import { firstOrDefault, none, where } from "../../../framework/utils/collection";
import { PresenterAccountEvents } from "../events";
import { IAccountPreferencesFormModel, IAccountPreferencesValueModel, IAccountPreferencesModelChange } from "./account-preferences-iface";
import { xlate, xlationContext } from "../../../framework/utils/i18n";
import { browserUses12HourTime } from "../../../framework/utils/device";
import { IUpdateAccountPreferencesRequest } from "../../../entities-generated/models/presenter-account/update-account-preferences-request";
import { PresenterAccountService } from "../../../services/account";

// ##BEGIN:DEVELOPERCODE##

xlationContext('Presenter account preferences form service');

export const timeDisplayOptions: IBwjFormOption<TimeDisplayFormat>[] = [
    {
        value: TimeDisplayFormat.ClientDefault,
        description: browserUses12HourTime() ? xlate('Browser default (12-hour)') : xlate('Browser default (24-hour)'),
    }, {
        value: TimeDisplayFormat.Hour12,
        description: xlate('12-hour (e.g. 5:00 AM / 5:00 PM)'),
    }, {
        value: TimeDisplayFormat.Hour24,
        description: xlate('24-hour (e.g. 05:00 / 17:00)'),
    },
];

// ##END:DEVELOPERCODE##

// %%BEGIN:timeDisplayFormat/validated/IBwjFormOption<TimeDisplayFormat>/TimeDisplayFormat%%
export const timeDisplayFormat = new (class timeDisplayFormatService extends ValidatedFormValueService<IPresenterAccountPreferences, IBwjFormOption<TimeDisplayFormat>, TimeDisplayFormat> {
    public initialize(data: IPresenterAccountPreferences): void {
        const opt = firstOrDefault(timeDisplayOptions, o => o.value === data.timeDisplayFormat) ?? timeDisplayOptions[0];
        this.setInputValue(new BwjInput<IBwjFormOption<TimeDisplayFormat>>(opt));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IPresenterAccountPreferences): void {
        data.timeDisplayFormat = this.value.value.value;
    }

    public getDefaultValue(): BwjInput<IBwjFormOption<TimeDisplayFormat>> {
        return new BwjInput<IBwjFormOption<TimeDisplayFormat>>(timeDisplayOptions[0]);
    }

    public getInputValue(): BwjInput<IBwjFormOption<TimeDisplayFormat>> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IBwjFormOption<TimeDisplayFormat>>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): TimeDisplayFormat {
        const value = this.value.value;
        return value.value;
    }

    public validate(value: IBwjFormOption<TimeDisplayFormat>): Promise<BwjInput<IBwjFormOption<TimeDisplayFormat>>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: IBwjFormOption<TimeDisplayFormat> | null): void {
        value = value ?? timeDisplayOptions[0];
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }
})(
    'timeDisplayFormat' // id
    ,xlate('Time display format') // description
);
// %%END:timeDisplayFormat/validated/IBwjFormOption<TimeDisplayFormat>/TimeDisplayFormat%%

// %%BEGIN:fontSizePreference/mutable/number/FontSizePreference%%
export const fontSizePreference = new (class fontSizePreferenceService extends MutableFormValueService<IPresenterAccountPreferences, number, FontSizePreference> {
    public initialize(data: IPresenterAccountPreferences): void {
        this.value = data.fontSizePreference;
    }

    public applyToDataModel(data: IPresenterAccountPreferences): void {
        data.fontSizePreference = this.value;
    }

    public getDefaultValue(): number {
        return 0;
    }

    public getInputValue(): number {
        return this.value;
    }

    public setInputValue(value: number): void {
        this.value = value;
    }

    public getOutputValue(): FontSizePreference {
        const value = this.value;
        return value as FontSizePreference;
    }

    public onUpdate(value: number): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'fontSizePreference' // id
    ,xlate('Discussion plan text size') // description
);
// %%END:fontSizePreference/mutable/number/FontSizePreference%%

// %%BEGIN:warningColorWhenTimeRemainingAt/mutable/number/number%%
export const warningColorWhenTimeRemainingAt = new (class warningColorWhenTimeRemainingAtService extends MutableFormValueService<IPresenterAccountPreferences, number, number> {
    public initialize(data: IPresenterAccountPreferences): void {
        this.value = 100 - data.warningColorAfterPercentElasped;
    }

    public applyToDataModel(data: IPresenterAccountPreferences): void {
        data.warningColorAfterPercentElasped = 100 - this.value;
    }

    public getDefaultValue(): number {
        return 0;
    }

    public getInputValue(): number {
        return this.value;
    }

    public setInputValue(value: number): void {
        this.value = value;
    }

    public getOutputValue(): number {
        return 100 - this.value;
    }

    public onUpdate(value: number): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }

    public format(value: number) {
        return value !== 0 ? `${value}%` : xlate('Never');
    }
})(
    'warningColorWhenTimeRemainingAt' // id
    ,xlate('Show warning color when time remaining at') // description
);
// %%END:warningColorWhenTimeRemainingAt/mutable/number/number%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IAccountPreferencesFormModel => {
    return {
       timeDisplayFormat: timeDisplayFormat.getInputValue(),
       fontSizePreference: fontSizePreference.getInputValue(),
       warningColorWhenTimeRemainingAt: warningColorWhenTimeRemainingAt.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IAccountPreferencesValueModel => {
    return {
       timeDisplayFormat: timeDisplayFormat.getOutputValue(),
       fontSizePreference: fontSizePreference.getOutputValue(),
       warningColorWhenTimeRemainingAt: warningColorWhenTimeRemainingAt.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        timeDisplayFormat.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IAccountPreferencesModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(PresenterAccountEvents.FormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IAccountPreferencesFormModel>> => {
    const promises = [
        timeDisplayFormat.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IAccountPreferencesFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IAccountPreferencesValueModel, dataModel: IPresenterAccountPreferences } = {
    dataModel: new PresenterAccountPreferences()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IPresenterAccountPreferences) => {
    const dataModel = data || new PresenterAccountPreferences();

    timeDisplayFormat.initialize(dataModel);
    fontSizePreference.initialize(dataModel);
    warningColorWhenTimeRemainingAt.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IAccountPreferencesFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
timeDisplayFormat.setChangePublisher(broadcastChange);
fontSizePreference.setChangePublisher(broadcastChange);
warningColorWhenTimeRemainingAt.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new PresenterAccountPreferences();

    timeDisplayFormat.applyToDataModel(dataModel);
    fontSizePreference.applyToDataModel(dataModel);
    warningColorWhenTimeRemainingAt.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IAccountPreferencesModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], PresenterAccountEvents.FormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IAccountPreferencesFormModel => {
    const [getter, setter] = useState<IAccountPreferencesFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IAccountPreferencesModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IAccountPreferencesFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('AccountPreferencesFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('AccountPreferencesFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

let preferencesInitialized = false;
export const initializePreferences = () => {
    if(preferencesInitialized) { return; }

    const dataModel = PresenterAccountService.myPreferences();
    if(!!dataModel) {
        initializeService(dataModel);
        preferencesInitialized = true;
    }
}

export const setSavePoint = () => {
    setReversionState();
    revertChanges();
}

export const toRequest = () => {
    const v = toValueModel();
    const request: IUpdateAccountPreferencesRequest = {
        timeDisplayFormat: v.timeDisplayFormat,
        fontSizePreference: v.fontSizePreference,
        warningColorAfterPercentElasped: v.warningColorWhenTimeRemainingAt,
    };

    return request;
}

export const toModel = () => {
    const v = toValueModel();
    const request: IPresenterAccountPreferences = {
        timeDisplayFormat: v.timeDisplayFormat,
        fontSizePreference: v.fontSizePreference,
        warningColorAfterPercentElasped: v.warningColorWhenTimeRemainingAt,
    };

    return request;
}

// ##END:DEVELOPERCODE##
